import { flow, Instance, SnapshotIn, types } from 'mobx-state-tree';
import { ICallerRequest } from '../../layouts/authorized/components/call-card/types';
import { ITag } from '../../views/authorized/pages/telephony/types';
import { Telephony } from '../../http-requests';
import { IParams } from '../../http-requests/types';

const CallDirectionMST = types.enumeration('CallDirection', [
  'INBOUND',
  'OUTBOUND',
  'INWARD',
]);
export type CallDirection = SnapshotIn<typeof CallDirectionMST>;

const CallStatusMST = types.enumeration('CallStatus', [
  'CREATE',
  'ANSWER',
  'IN_PROCESS',
  'FORWARD',
  'END',
]);
export type CallStatus = SnapshotIn<typeof CallStatusMST>;

const TabTypeMST = types.enumeration('TabType', ['main', 'meters', 'other']);
export type TabType = SnapshotIn<typeof TabTypeMST>;

const MetersTabTypeMST = types.enumeration('MetersTabType', [
  'address',
  'applicant',
  'meters',
  'end',
]);
export type MetersStepType = SnapshotIn<typeof MetersTabTypeMST>;

const OthersTabTypeMST = types.enumeration('MetersTabType', [
  'subscriber',
  'register',
]);
export type OthersStepType = SnapshotIn<typeof OthersTabTypeMST>;

const IdAndNameModel = types.model({
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
});

const PhoneModel = types.model({
  phone_type: types.optional(types.string, ''),
  code: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  add: types.optional(types.maybeNull(types.string), ''),
});

export interface IPhoneСС extends Instance<typeof PhoneModel> {}

const CallerModel = types.model({
  id: types.optional(types.maybeNull(types.string), ''),
  name: types.optional(types.string, ''),
  phone_number: types.optional(types.string, ''),
  phone_add: types.optional(types.maybeNull(types.string), ''),
  address: types.optional(types.string, ''),
  _type: types.optional(types.array(types.string), []),
});

export interface ICallerСС extends Instance<typeof CallerModel> {}

const ProviderModel = types.model({
  str_name: types.optional(types.maybeNull(types.string), ''),
});

const InfoModel = types.model({
  id: types.optional(types.string, ''),
  phone_number: types.optional(PhoneModel, {}),
  _type: types.optional(types.string, ''),
});

const DepartmentModel = types.model({
  name: types.optional(types.maybeNull(types.string), ''),
});

const PositionModel = types.model({
  name: types.optional(types.maybeNull(types.string), ''),
});

const TenantModel = types.model({
  id: types.optional(types.string, ''),
  address: types.optional(types.maybeNull(types.string), ''),
});

export interface ITenantСС extends Instance<typeof TenantModel> {}

const WorkerModel = types.model({
  id: types.optional(types.string, ''),
  provider: types.optional(ProviderModel, {}),
  department: types.optional(DepartmentModel, {}),
  position: types.optional(PositionModel, {}),
});

export interface IWorkerСС extends Instance<typeof WorkerModel> {}

const SectorModel = types.model({
  code: types.optional(types.string, ''),
  value: types.optional(types.number, 0),
});

const TagModel = types.model({
  author: types.optional(IdAndNameModel, {}),
  date: types.optional(types.string, ''),
  tag: types.optional(types.string, ''),
});

const CallerInfoModel = types
  .model({
    name: types.optional(types.string, ''),
    email: types.optional(types.string, ''),
    phone_number: types.optional(types.string, ''),
    first_number: types.optional(types.string, ''),
    address: types.optional(types.string, ''),
    strNumber: types.optional(types.string, ''),
  })
  .actions((self) => ({
    setCallerActivePhone(str: string) {
      self.first_number = str;
    },

    setCallerPhone(str: string) {
      self.phone_number = str;
    },

    setCallerAddress(str: string) {
      self.address = str;
    },

    setCallerNumber(str: string) {
      self.strNumber = str;
    },

    setCallerName(str: string) {
      self.name = str;
    },

    setCallerEmail(str: string) {
      self.email = str;
    },

    setCallerInfo(
      name: string,
      email: string,
      phone_number: string,
      address: string,
      number: string = ''
    ) {
      self.name = name;
      self.email = email;
      self.phone_number = phone_number;
      self.address = address;
      self.strNumber = number;
    },

    clearCallerInfo() {
      self.name = '';
      self.email = '';
      self.phone_number = '';
      self.first_number = '';
      self.address = '';
      self.strNumber = '';
    },
  }));

/** TODO: Надо объединять между собой TelephonyStore и CallCardStore в единый CallsStore
 * со своими сабмоделями. Слишком много бесполезных полей и методов, ужасная читаемость, плохая
 * типизация, разрозненность методов и моделей, по факту касающихся одной сущности - звонков. */
export const CallCard = types
  .model({
    id: types.optional(types.string, ''),
    status_call: types.optional(CallStatusMST, 'CREATE'),
    direction: types.optional(CallDirectionMST, 'INBOUND'),
    duration: types.optional(types.number, 0),
    caller: types.optional(CallerModel, {}),
    tenantChoices: types.optional(types.array(TenantModel), []),
    workerChoices: types.optional(types.array(WorkerModel), []),
    currentAddress: types.optional(TenantModel, {
      id: 'select_address',
      address: 'Выберите адрес',
    }),
    sectors: types.optional(types.array(SectorModel), []),
    currentSector: types.optional(SectorModel, {}),
    currentProvider: types.optional(WorkerModel, {}),
    loading: types.optional(types.boolean, false),
    collapsed: types.optional(types.boolean, false),
    tag: types.optional(types.maybeNull(TagModel), {}),
    info: types.optional(types.array(InfoModel), []),
    tab: types.optional(TabTypeMST, 'main'),
    metersTab: types.optional(MetersTabTypeMST, 'address'),
    othersTab: types.optional(OthersTabTypeMST, 'subscriber'),
    callerInfo: types.optional(CallerInfoModel, {}),
    // Время начала обратного отсчёта (для END статуса)
    endCallTimestamp: types.maybeNull(types.number),
    wasAnswered: types.optional(types.boolean, false),
    // Timestamp начала звонка для точного подсчета времени
    callStartTimestamp: types.maybeNull(types.number),
  })
  .views((self) => ({
    get multipleProviders() {
      return self.workerChoices.length > 2;
    },
    get active() {
      return self.status_call === 'ANSWER';
    },
    get shouldShowFullCard(): boolean {
      if (!self.wasAnswered) {
        return false;
      }

      return !['CREATE', 'IN_PROCESS'].includes(self.status_call);
    },
    /**
     * Обратный отсчёт до автозакрытия (в секундах)
     * Возвращает null если звонок не завершён или время истекло
     */
    get autoCloseCountdown(): number | null {
      if (self.status_call !== 'END' || !self.endCallTimestamp) {
        return null;
      }

      const elapsed = Date.now() - self.endCallTimestamp;
      const remaining = Math.max(0, 10 - Math.floor(elapsed / 1000));

      return remaining > 0 ? remaining : null;
    },
    /**
     * Показывать ли таймер обратного отсчёта
     */
    get showCountdown(): boolean {
      return this.autoCloseCountdown !== null;
    },
    setStatusCall(status: CallStatus) {
      // Помечаем что звонок был принят
      if (status === 'ANSWER') {
        self.wasAnswered = true;
      }

      self.status_call = status;
    },
  }))
  .actions((self) => ({
    getCallData(data: any) {
      self.id = data.id;
      self.caller.phone_number = data.caller.phone_number;
      self.caller.phone_add = data.caller.phone_add || null;
      self.status_call = data.status_call;
      self.direction = data.direction;
      self.duration = data.billsec;
      self.tag = data.tag;
      self.setStatusCall(data.status_call);

      // Если звонок активен и есть начальная длительность,
      // вычисляем timestamp начала для продолжения отсчета
      if (data.status_call === 'ANSWER' && data.billsec > 0) {
        self.callStartTimestamp = Date.now() - data.billsec * 1000;
      }
    },
    updateStatusCall(status_call: CallStatus) {
      const prevStatus = self.status_call;
      self.status_call = status_call;

      self.setStatusCall(status_call);

      // При ответе на звонок запускаем таймер
      if (status_call === 'ANSWER' && prevStatus !== 'ANSWER') {
        self.callStartTimestamp = Date.now();
        self.duration = 0;
      }

      // При завершении звонка запоминаем время для обратного отсчёта
      if (status_call === 'END' && prevStatus !== 'END') {
        self.endCallTimestamp = Date.now();
        self.callStartTimestamp = null;
      }
    },
    clearChoices(type: string) {
      if (type === 'Worker') {
        self.workerChoices.length = 0;
      } else {
        self.tenantChoices.length = 0;
      }
    },
    setCollapsed(value?: boolean) {
      self.collapsed = value ?? !self.collapsed;
    },
    increaseTimer() {
      self.duration++;
    },
    updateTimerFromTimestamp() {
      // Вычисляем длительность на основе реального времени
      if (self.callStartTimestamp) {
        const elapsed = Math.floor(
          (Date.now() - self.callStartTimestamp) / 1000
        );

        // Обновляем только если секунды изменились (оптимизация рендеринга)
        if (elapsed !== self.duration) {
          self.duration = elapsed;
        }
      }
    },
    startCallTimer() {
      // Запоминаем время начала звонка
      self.callStartTimestamp = Date.now();
      self.duration = 0;
    },
    stopCallTimer() {
      // Останавливаем таймер, но сохраняем последнее значение duration
      self.callStartTimestamp = null;
    },
    setCurrentAddress(address: ITenantСС) {
      self.currentAddress = { ...address };
    },
    setTab(tab: TabType) {
      self.tab = tab;
    },
    setMetersTab(tab: MetersStepType) {
      self.metersTab = tab;
    },
    setOthersTab(tab: OthersStepType) {
      self.othersTab = tab;
    },
    getCallerData: flow(function* (id: string, phone: IParams) {
      try {
        if (self.loading || self.caller.name.length) {
          return;
        }

        self.loading = true;

        const response = yield Telephony.GetCallerData(id, phone);
        const data = response.data;

        if (data) {
          self.caller.id = data[0]?.id || null;
          self.caller.name = data[0]?.name || 'Неизвестный абонент';
          self.caller.address = data[0]?.address;
          self.caller._type = data?.map((item: ICallerRequest) => {
            return item._type;
          });

          self.workerChoices = data
            ?.filter((item: ICallerRequest) => item._type === 'Worker')
            ?.map((item: ICallerRequest): IWorkerСС => {
              self.info.push({
                id: item.id,
                phone_number: {
                  phone_type: item.phone_number[0].phone_type,
                  code: item.phone_number[0].code,
                  number: item.phone_number[0].number,
                  add: item.phone_number[0].add,
                },
                _type: item._type,
              });

              return {
                id: item.id,
                provider: { str_name: item.provider },
                department: { name: item.department },
                position: { name: item.position },
              };
            });
          self.workerChoices.push({
            id: 'other_provider',
            provider: { str_name: 'Другая организация' },
          });

          self.tenantChoices = data
            ?.filter((item: ICallerRequest) => item._type === 'Tenant')
            ?.map((item: ICallerRequest): ITenantСС => {
              self.info.push({
                id: item.id,
                phone_number: {
                  phone_type: item.phone_number[0].phone_type,
                  code: item.phone_number[0].code,
                  number: item.phone_number[0].number,
                  add: item.phone_number[0].add,
                },
                _type: item._type,
              });

              return {
                id: item.id,
                address: item.address,
              };
            });
          self.tenantChoices.push({
            id: 'other_address',
            address: 'Другой адрес',
          });

          if (data[0]?._type === 'Worker') {
            self.currentProvider = {
              id: self.workerChoices[0].id,
              provider: { str_name: self.workerChoices[0].provider.str_name },
              department: { name: self.workerChoices[0].department.name },
              position: { name: self.workerChoices[0].position.name },
            };
          }

          if (data[0]?._type === 'Tenant') {
            self.currentAddress = {
              id: self.tenantChoices[0].id,
              address: self.tenantChoices[0].address,
            };
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        self.loading = false;
      }
    }),
    updateTag(tag: ITag) {
      self.tag = tag;
    },

    updateCaller: flow(function* (telephonyStore: any) {
      const params = {
        name: self.callerInfo.name,
        email: self.callerInfo.email,
        phone_number: self.callerInfo.phone_number,
        address: self.callerInfo.address + ' ' + self.callerInfo.strNumber,
      };

      if (!params.name) {
        return;
      }

      try {
        // Используем метод telephonyStore для обновления журнала звонков
        yield telephonyStore.changeCaller(self.id, params);
        // Очищаем локальный callerInfo после успешного обновления
        self.callerInfo.clearCallerInfo();
      } catch (e) {
        console.error('Error updating caller in CallCard:', e);
      }
    }),
  }));

export interface ICallCard extends Instance<typeof CallCard> {}

// Тестовые данные, чтоб не звонить каждый раз
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TEST_CALLS_ARRAY = [
  {
    id: '6613bbe83b73a1000b014a1d',
    status_call: 'ANSWER',
    direction: 'INBOUND',
    duration: 0,
    caller: {
      id: null,
      name: 'Неизвестный абонент',
      phone_number: '9537505340', //9537505340
      //9990297300 воркер
      //9110226598 жилец
      //9994535452  сторонее
      //9537505340 нонейм
      _type: [],
    },
    tenantChoices: [
      {
        id: 'other_address',
        address: 'Другой адрес',
      },
    ],
    workerChoices: [
      {
        id: 'other_provider',
        provider: {
          str_name: 'Другая организация',
        },
        department: {
          name: 'Buhgalteria',
        },
        position: {
          name: 'GlavBuh',
        },
      },
    ],
    currentAddress: {
      id: 'select_address',
      address: 'Выберите адрес',
    },
    sectors: [],
    currentSector: {
      code: '',
      value: 0,
    },
    currentProvider: {
      id: '',
      provider: {
        str_name: '',
      },
      department: {
        name: '',
      },
      position: {
        name: '',
      },
    },
    loading: false,
    collapsed: false,
    tag: {
      author: {
        id: '',
        name: '',
      },
      date: '',
      tag: '',
    },
    info: [],
  },
  {
    id: '6613bbe83b73a1000b014a12',
    status_call: 'CREATE',
    direction: 'INBOUND',
    duration: 0,
    caller: {
      id: null,
      name: 'Неизвестный абонент',
      phone_number: '9990297300',
      _type: [],
    },
    tenantChoices: [
      {
        id: 'other_address',
        address: 'Другой адрес',
      },
    ],
    workerChoices: [
      {
        id: 'other_provider',
        provider: {
          str_name: 'Другая организация',
        },
        department: {
          name: '',
        },
        position: {
          name: '',
        },
      },
    ],
    currentAddress: {
      id: 'select_address',
      address: 'Выберите адрес',
    },
    sectors: [],
    currentSector: {
      code: '',
      value: 0,
    },
    currentProvider: {
      id: '',
      provider: {
        str_name: '',
      },
      department: {
        name: '',
      },
      position: {
        name: '',
      },
    },
    loading: false,
    collapsed: false,
    tag: {
      author: {
        id: '',
        name: '',
      },
      date: '',
      tag: '',
    },
    info: [],
  },
] as unknown as ICallCard[];

export const CallCardStore = types
  .model({
    cards: types.optional(types.array(CallCard), []),
  })
  .views((self) => ({
    get activeCard(): ICallCard | null {
      return self.cards.length ? self.cards[self.cards.length - 1] : null;
    },
    getCardById(id: string): ICallCard | undefined {
      return self.cards.find((card) => card.id === id);
    },
  }))
  .actions((self) => ({
    createNewCard() {
      self.cards.unshift({});
    },
    removeCard(id: string) {
      // Находим карточку и сбрасываем все timestamp перед удалением
      const card = self.cards.find((c) => c.id === id);

      if (card) {
        // Очищаем все таймеры
        if (card.endCallTimestamp) {
          card.endCallTimestamp = null;
        }

        if (card.callStartTimestamp) {
          card.callStartTimestamp = null;
        }
      }

      self.cards.replace(self.cards.filter((card) => card.id !== id));
    },
    /**
     * Удаляет все завершённые карточки (со статусом END)
     * Используется при поступлении нового звонка, чтобы старые завершённые
     * карточки не мешали определению актуального звонка
     */
    removeEndedCards() {
      // Очищаем таймеры у завершённых карточек перед удалением
      self.cards
        .filter((card) => card.status_call === 'END')
        .forEach((card) => {
          if (card.endCallTimestamp) {
            card.endCallTimestamp = null;
          }

          if (card.callStartTimestamp) {
            card.callStartTimestamp = null;
          }
        });

      self.cards.replace(
        self.cards.filter((card) => card.status_call !== 'END')
      );
    },
    markInProcessCardAsEnded(id: string) {
      const card = self.cards.find((c) => c.id === id);

      if (!card || card.status_call !== 'IN_PROCESS') {
        return;
      }

      card.updateStatusCall('END');
    },
    goToRobot: flow(function* () {
      try {
        const response = yield Telephony.GoToRobot(self.activeCard?.id || '');

        return response;
      } catch (e) {
        console.error(e);
      } finally {
        //...
      }
    }),
  }));
